import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit, Trash2, Power, PowerOff, Zap, Mail, MessageSquare,
  Phone, ChevronDown, ChevronRight, Clock, Calendar, Settings2,
  Save, X, AlertCircle, CheckCircle2, Copy, Play, Eye, Users,
  AlertTriangle, Filter, RotateCw
} from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import EmailEditor from '@/components/EmailEditor';
import { SmsEditorWithTags, EmailSubjectWithTags, VariableHighlighter } from '@/components/VariableSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OPERATORS = [
  { value: 'equals', label: 'Est égal à' },
  { value: 'not_equals', label: 'N\'est pas égal à' },
  { value: 'contains', label: 'Contient' },
  { value: 'not_contains', label: 'Ne contient pas' },
  { value: 'starts_with', label: 'Commence par' },
  { value: 'ends_with', label: 'Se termine par' },
  { value: 'is_empty', label: 'Est vide' },
  { value: 'is_not_empty', label: 'N\'est pas vide' },
];

const TIME_UNITS = [
  { value: 'days', label: 'Jours' },
  { value: 'weeks', label: 'Semaines' },
  { value: 'months', label: 'Mois' },
];

const AVAILABLE_VARIABLES = [
  '{client_name}', '{client_email}', '{client_phone}',
  '{status}', '{date}', '{company_name}'
];

const AdvancedAutomations = () => {
  const [activeTab, setActiveTab] = useState('automations');
  const [automations, setAutomations] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [sheetColumns, setSheetColumns] = useState([]);
  const [sheetSettings, setSheetSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const [isAutomationDialogOpen, setIsAutomationDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isSmsDialogOpen, setIsSmsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);

  // Part 1 — import result modal
  const [importResult, setImportResult] = useState(null);
  const [isImportResultOpen, setIsImportResultOpen] = useState(false);
  const [importErrorExpanded, setImportErrorExpanded] = useState(false);

  // Part 3 — bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [resetTrackingTarget, setResetTrackingTarget] = useState(null);

  // Part 4 — quick filters
  const [activeFilter, setActiveFilter] = useState('all');

  const [automationForm, setAutomationForm] = useState({
    name: '',
    description: '',
    is_active: true,
    trigger_mode: 'immediate',
    condition_groups: [{ conditions: [{ column_name: '', operator: 'equals', value: '' }], logical_operator: 'AND' }],
    groups_logical_operator: 'AND',
    time_based_config: null,
    actions: []
  });

  const [emailForm, setEmailForm] = useState({
    name: '',
    subject: '',
    html_content: '',
    text_content: ''
  });

  const [smsForm, setSmsForm] = useState({
    name: '',
    content: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [automationsRes, emailRes, smsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/automations`),
        axios.get(`${API}/automations/email-templates/list`),
        axios.get(`${API}/automations/sms-templates/list`),
        axios.get(`${API}/automations/sheet-settings`)
      ]);

      setAutomations(automationsRes.data);
      setEmailTemplates(emailRes.data);
      setSmsTemplates(smsRes.data);
      setSheetSettings(settingsRes.data || {});

      if (settingsRes.data?.spreadsheet_id) {
        const columnsRes = await axios.get(
          `${API}/automations/sheet-columns?spreadsheet_id=${settingsRes.data.spreadsheet_id}&sheet_name=${settingsRes.data.sheet_name || 'Sheet1'}`
        );
        setSheetColumns(columnsRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTemplates = useCallback(async () => {
    try {
      const [emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/automations/email-templates/list`),
        axios.get(`${API}/automations/sms-templates/list`)
      ]);
      setEmailTemplates(emailRes.data);
      setSmsTemplates(smsRes.data);
    } catch (error) {
      console.error('Error refreshing templates:', error);
    }
  }, []);

  const refreshAutomations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/automations`);
      setAutomations(res.data);
    } catch (error) {
      console.error('Error refreshing automations:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Derived: filtered list + counts ────────────────────────────────────
  const filterCounts = {
    all: automations.length,
    active: automations.filter(a => a.is_active).length,
    inactive: automations.filter(a => !a.is_active).length,
    broken: automations.filter(a => a.has_missing_templates).length,
  };

  const filteredAutomations = automations.filter(a => {
    if (activeFilter === 'active') return a.is_active;
    if (activeFilter === 'inactive') return !a.is_active;
    if (activeFilter === 'broken') return a.has_missing_templates;
    return true;
  });

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setSelectedIds(new Set());
  };

  // ─── Reset form ──────────────────────────────────────────────────────────
  const resetAutomationForm = () => {
    setAutomationForm({
      name: '',
      description: '',
      is_active: true,
      trigger_mode: 'immediate',
      condition_groups: [{ conditions: [{ column_name: '', operator: 'equals', value: '' }], logical_operator: 'AND' }],
      groups_logical_operator: 'AND',
      time_based_config: null,
      actions: []
    });
    setEditingItem(null);
  };

  // ─── Import handler (Part 1) ─────────────────────────────────────────────
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const res = await axios.post(`${API}/automations/import`, json);
      setImportResult(res.data);
      setImportErrorExpanded(false);
      setIsImportResultOpen(true);
    } catch (err) {
      toast.error('Fichier JSON invalide');
    }
    e.target.value = '';
  };

  const handleCloseImportResult = () => {
    setIsImportResultOpen(false);
    setImportResult(null);
    Promise.all([
      axios.get(`${API}/automations`).then(r => setAutomations(r.data)),
      axios.get(`${API}/automations/email-templates/list`).then(r => setEmailTemplates(r.data)),
      axios.get(`${API}/automations/sms-templates/list`).then(r => setSmsTemplates(r.data)),
    ]).catch(console.error);
  };

  const handleShowBrokenAutomations = () => {
    handleCloseImportResult();
    setActiveFilter('broken');
  };

  // ─── Automation CRUD ─────────────────────────────────────────────────────
  const handleSaveAutomation = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/automations/${editingItem.id}`, automationForm);
        toast.success('Automation mise à jour !');
      } else {
        await axios.post(`${API}/automations`, automationForm);
        toast.success('Automation créée !');
      }
      setIsAutomationDialogOpen(false);
      resetAutomationForm();
      refreshAutomations();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteAutomation = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette automation ?')) return;
    try {
      await axios.delete(`${API}/automations/${id}`);
      toast.success('Automation supprimée');
      refreshAutomations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleAutomation = async (id) => {
    try {
      await axios.post(`${API}/automations/${id}/toggle`, {});
      refreshAutomations();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleResetTracking = async () => {
    if (!resetTrackingTarget) return;
    try {
      const res = await axios.delete(`${API}/execution/${resetTrackingTarget.id}/fired-history`);
      const { deleted, re_executed, trigger_mode } = res.data;
      if (trigger_mode === 'immediate') {
        toast.success(
          `Tracking réinitialisé : ${deleted} entrée(s) supprimée(s). ` +
          `L'automation a été ré-exécutée contre ${re_executed} client(s).`
        );
      } else {
        toast.success(
          `Tracking réinitialisé : ${deleted} entrée(s) supprimée(s). ` +
          `L'automation sera ré-évaluée à la prochaine exécution programmée.`
        );
      }
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation du tracking');
    } finally {
      setResetTrackingTarget(null);
    }
  };

  // ─── Bulk operations (Part 3) ────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAutomations.length && filteredAutomations.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAutomations.map(a => a.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      const res = await axios.post(`${API}/automations/bulk-delete`, { ids: [...selectedIds] });
      toast.success(`${res.data.deleted} automation(s) supprimée(s)`);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} erreur(s) de suppression`);
      setSelectedIds(new Set());
      setIsBulkDeleteConfirmOpen(false);
      refreshAutomations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleBulkToggle = async (isActive) => {
    try {
      const res = await axios.post(`${API}/automations/bulk-update`, { ids: [...selectedIds], is_active: isActive });
      toast.success(`${res.data.updated} automation(s) ${isActive ? 'activée(s)' : 'désactivée(s)'}`);
      setSelectedIds(new Set());
      refreshAutomations();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // ─── Preview / Execute ───────────────────────────────────────────────────
  const handlePreviewAutomation = async (automationId) => {
    try {
      const response = await axios.get(`${API}/execution/preview/${automationId}?limit=20`);
      setPreviewData(response.data);
      setIsPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  const handleExecuteAutomation = async (automationId, dryRun = true) => {
    const automation = automations.find(a => a.id === automationId);

    if (!dryRun) {
      const confirmed = window.confirm(
        `⚠️ Êtes-vous sûr de vouloir exécuter l'automation "${automation?.name}" ?\n\nCette action enverra réellement des emails/SMS aux clients correspondants.`
      );
      if (!confirmed) return;
    }

    setIsExecuting(true);
    try {
      const response = await axios.post(
        `${API}/execution/run/${automationId}?dry_run=${dryRun}`,
        {}
      );

      const result = response.data;

      if (dryRun) {
        toast.success(`Simulation terminée: ${result.clients_matched} clients correspondent`);
      } else {
        toast.success(`Exécution terminée: ${result.clients_matched} clients traités`);
      }

      setPreviewData({
        ...result,
        automation_name: automation?.name,
        is_execution_result: true
      });
      setIsPreviewDialogOpen(true);

    } catch (error) {
      toast.error('Erreur lors de l\'exécution');
    } finally {
      setIsExecuting(false);
    }
  };

  // ─── Email / SMS templates ───────────────────────────────────────────────
  const handleSaveEmailTemplate = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/automations/email-templates/${editingItem.id}`, emailForm);
        toast.success('Template email mis à jour !');
      } else {
        await axios.post(`${API}/automations/email-templates`, emailForm);
        toast.success('Template email créé !');
      }
      setIsEmailDialogOpen(false);
      setEmailForm({ name: '', subject: '', html_content: '', text_content: '' });
      setEditingItem(null);
      refreshTemplates();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteEmailTemplate = async (id) => {
    if (!window.confirm('Supprimer ce template email ?')) return;
    try {
      await axios.delete(`${API}/automations/email-templates/${id}`);
      toast.success('Template supprimé');
      refreshTemplates();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveSmsTemplate = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/automations/sms-templates/${editingItem.id}`, smsForm);
        toast.success('Template SMS mis à jour !');
      } else {
        await axios.post(`${API}/automations/sms-templates`, smsForm);
        toast.success('Template SMS créé !');
      }
      setIsSmsDialogOpen(false);
      setSmsForm({ name: '', content: '' });
      setEditingItem(null);
      refreshTemplates();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteSmsTemplate = async (id) => {
    if (!window.confirm('Supprimer ce template SMS ?')) return;
    try {
      await axios.delete(`${API}/automations/sms-templates/${id}`);
      toast.success('Template supprimé');
      refreshTemplates();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ─── Edit openers ────────────────────────────────────────────────────────
  const openEditAutomation = (automation) => {
    setEditingItem(automation);
    setAutomationForm({
      name: automation.name,
      description: automation.description || '',
      is_active: automation.is_active,
      trigger_mode: automation.trigger_mode,
      condition_groups: automation.condition_groups?.length > 0
        ? automation.condition_groups
        : [{ conditions: [{ column_name: '', operator: 'equals', value: '' }], logical_operator: 'AND' }],
      groups_logical_operator: automation.groups_logical_operator || 'AND',
      time_based_config: automation.time_based_config,
      actions: automation.actions || []
    });
    setIsAutomationDialogOpen(true);
  };

  const openEditEmailTemplate = (template) => {
    setEditingItem(template);
    setEmailForm({
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      text_content: template.text_content || ''
    });
    setIsEmailDialogOpen(true);
  };

  const openEditSmsTemplate = (template) => {
    setEditingItem(template);
    setSmsForm({
      name: template.name,
      content: template.content
    });
    setIsSmsDialogOpen(true);
  };

  // ─── Condition group helpers ─────────────────────────────────────────────
  const addConditionGroup = () => {
    setAutomationForm(prev => ({
      ...prev,
      condition_groups: [
        ...prev.condition_groups,
        { conditions: [{ column_name: '', operator: 'equals', value: '' }], logical_operator: 'AND' }
      ]
    }));
  };

  const removeConditionGroup = (groupIndex) => {
    setAutomationForm(prev => ({
      ...prev,
      condition_groups: prev.condition_groups.filter((_, i) => i !== groupIndex)
    }));
  };

  const addCondition = (groupIndex) => {
    setAutomationForm(prev => {
      const newGroups = [...prev.condition_groups];
      newGroups[groupIndex].conditions.push({ column_name: '', operator: 'equals', value: '' });
      return { ...prev, condition_groups: newGroups };
    });
  };

  const removeCondition = (groupIndex, conditionIndex) => {
    setAutomationForm(prev => {
      const newGroups = [...prev.condition_groups];
      newGroups[groupIndex].conditions = newGroups[groupIndex].conditions.filter((_, i) => i !== conditionIndex);
      return { ...prev, condition_groups: newGroups };
    });
  };

  const updateCondition = (groupIndex, conditionIndex, field, value) => {
    setAutomationForm(prev => {
      const newGroups = [...prev.condition_groups];
      newGroups[groupIndex].conditions[conditionIndex][field] = value;
      return { ...prev, condition_groups: newGroups };
    });
  };

  const addAction = (type) => {
    setAutomationForm(prev => ({
      ...prev,
      actions: [...prev.actions, { action_type: type, template_id: '', is_enabled: true }]
    }));
  };

  const removeAction = (index) => {
    setAutomationForm(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  const updateAction = (index, field, value) => {
    setAutomationForm(prev => {
      const newActions = [...prev.actions];
      newActions[index][field] = value;
      return { ...prev, actions: newActions };
    });
  };

  const getSmsCharCount = (text) => {
    const count = text.length;
    const segments = count <= 160 ? 1 : Math.ceil(count / 153);
    return { count, segments };
  };

  // ─── Duplicate handlers ──────────────────────────────────────────────────
  const handleDuplicateAutomation = async (automation) => {
    try {
      const newData = {
        name: `${automation.name} (copie)`,
        description: automation.description || '',
        is_active: false,
        trigger_mode: automation.trigger_mode,
        condition_groups: automation.condition_groups || [],
        groups_logical_operator: automation.groups_logical_operator || 'AND',
        time_based_config: automation.time_based_config,
        actions: automation.actions || []
      };
      await axios.post(`${API}/automations`, newData);
      toast.success('Automation dupliquée !');
      refreshAutomations();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDuplicateEmailTemplate = async (template) => {
    try {
      await axios.post(`${API}/automations/email-templates`, {
        name: `${template.name} (copie)`,
        subject: template.subject,
        html_content: template.html_content,
        text_content: template.text_content || ''
      });
      toast.success('Template email dupliqué !');
      refreshTemplates();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDuplicateSmsTemplate = async (template) => {
    try {
      await axios.post(`${API}/automations/sms-templates`, {
        name: `${template.name} (copie)`,
        content: template.content
      });
      toast.success('Template SMS dupliqué !');
      refreshTemplates();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  // ─── Test send handlers ──────────────────────────────────────────────────
  const handleTestEmail = async (template) => {
    const recipient = window.prompt('Email du destinataire pour le test :');
    if (!recipient) return;
    try {
      await axios.post(`${API}/admin/test-email`, {
        recipient,
        recipient_name: 'Test',
        subject: template.subject,
        content: template.html_content,
        text_content: template.text_content || ''
      });
      toast.success(`Email de test envoyé à ${recipient}`);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du test email. Vérifiez vos identifiants Mailjet dans les Paramètres.');
    }
  };

  const handleTestSms = async (template) => {
    const recipient = window.prompt('Numéro de téléphone pour le test (format +33...) :');
    if (!recipient) return;
    try {
      await axios.post(`${API}/admin/test-sms`, {
        recipient,
        message: template.content
      });
      toast.success(`SMS de test envoyé à ${recipient}`);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du test SMS. Vérifiez vos identifiants Twilio dans les Paramètres.');
    }
  };

  const insertVariable = (variable, field) => {
    if (field === 'sms') {
      setSmsForm(prev => ({ ...prev, content: prev.content + variable }));
    } else if (field === 'email_subject') {
      setEmailForm(prev => ({ ...prev, subject: prev.subject + variable }));
    } else if (field === 'email_content') {
      setEmailForm(prev => ({ ...prev, html_content: prev.html_content + variable }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const allFilteredSelected =
    filteredAutomations.length > 0 &&
    filteredAutomations.every(a => selectedIds.has(a.id));

  return (
    <TooltipProvider>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold font-heading tracking-tight text-foreground">
              Automations
            </h1>
            <p className="text-muted-foreground mt-2 leading-relaxed text-sm sm:text-base">
              Configurez vos triggers et actions automatisées
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="automations" className="flex items-center gap-2" data-testid="tab-automations">
              <Zap size={16} />
              <span className="hidden sm:inline">Automations</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2" data-testid="tab-email">
              <Mail size={16} />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="sms" className="flex items-center gap-2" data-testid="tab-sms">
              <MessageSquare size={16} />
              <span className="hidden sm:inline">SMS</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="automations" className="mt-6">
            {/* Top bar: import + create */}
            <div className="flex justify-end gap-2 mb-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={handleImport}
                  data-testid="import-json-input"
                />
                <Button variant="outline" className="rounded-full" asChild>
                  <span><Plus className="mr-2 h-4 w-4" />Importer JSON</span>
                </Button>
              </label>
              <Button
                onClick={() => { resetAutomationForm(); setIsAutomationDialogOpen(true); }}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                data-testid="add-automation-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Nouvelle Automation
              </Button>
            </div>

            {/* Part 4 — Quick filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key: 'all', label: 'Toutes' },
                { key: 'active', label: 'Actives' },
                { key: 'inactive', label: 'Inactives' },
                { key: 'broken', label: 'Cassées' },
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  variant={activeFilter === key ? 'default' : 'outline'}
                  size="sm"
                  className={`rounded-full ${key === 'broken' && activeFilter === key ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  onClick={() => handleFilterChange(key)}
                  data-testid={`filter-${key}`}
                >
                  {key === 'broken' && <AlertTriangle size={13} className="mr-1" />}
                  {label}
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                    activeFilter === key ? 'bg-white/20 text-inherit' : 'bg-muted text-muted-foreground'
                  }`}>
                    {filterCounts[key]}
                  </span>
                </Button>
              ))}
            </div>

            {/* Part 3 — Select all row */}
            {filteredAutomations.length > 0 && (
              <div className="flex items-center gap-3 mb-3 px-1">
                <Checkbox
                  checked={allFilteredSelected}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm text-muted-foreground">
                  {allFilteredSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </span>
              </div>
            )}

            {/* Automation list */}
            <div className="space-y-4">
              {filteredAutomations.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="text-center py-12">
                    <Zap className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">
                      {activeFilter === 'all' ? 'Aucune automation configurée' : 'Aucune automation dans ce filtre'}
                    </p>
                    {activeFilter === 'all' && (
                      <p className="text-sm text-muted-foreground/60 mt-1">Créez votre première automation pour commencer</p>
                    )}
                  </CardContent>
                </Card>
              ) : (
                filteredAutomations.map((automation) => (
                  <Card
                    key={automation.id}
                    className={`hover:shadow-md transition-shadow ${selectedIds.has(automation.id) ? 'ring-2 ring-primary' : ''}`}
                    data-testid={`automation-${automation.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        {/* Part 3 — checkbox */}
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedIds.has(automation.id)}
                            onCheckedChange={() => toggleSelect(automation.id)}
                            className="mt-1"
                            data-testid={`checkbox-${automation.id}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              <h3 className="text-xl font-semibold">{automation.name}</h3>
                              <Badge variant={automation.is_active ? "default" : "secondary"}>
                                {automation.is_active ? (
                                  <><Power size={12} className="mr-1" /> Actif</>
                                ) : (
                                  <><PowerOff size={12} className="mr-1" /> Inactif</>
                                )}
                              </Badge>
                              <Badge variant="outline">
                                {automation.trigger_mode === 'time_based' ? (
                                  <><Clock size={12} className="mr-1" /> Temporel</>
                                ) : (
                                  <><Zap size={12} className="mr-1" /> Immédiat</>
                                )}
                              </Badge>
                              {/* Part 2 — missing template badge */}
                              {automation.has_missing_templates && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="destructive"
                                      className="cursor-default"
                                      data-testid={`badge-missing-${automation.id}`}
                                    >
                                      <AlertTriangle size={12} className="mr-1" />
                                      Template manquant
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    Cette automation a un ou plusieurs templates email référencés qui n'existent pas. Elle ne pourra pas s'exécuter.
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            {automation.description && (
                              <p className="text-muted-foreground mt-2 text-sm">{automation.description}</p>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2">
                              {automation.actions?.map((action, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {action.action_type === 'email' && <Mail size={12} className="mr-1" />}
                                  {action.action_type === 'sms' && <MessageSquare size={12} className="mr-1" />}
                                  {action.action_type === 'ai_call' && <Phone size={12} className="mr-1" />}
                                  {action.action_type.toUpperCase()}
                                </Badge>
                              ))}
                            </div>
                            {automation.last_triggered_at && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Dernière exécution: {new Date(automation.last_triggered_at).toLocaleString('fr-FR')}
                                {automation.trigger_count > 0 && ` • ${automation.trigger_count} déclenchements`}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row items-center gap-2 ml-7 sm:ml-0">
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePreviewAutomation(automation.id)}
                              title="Prévisualiser"
                              data-testid={`preview-${automation.id}`}
                            >
                              <Eye size={16} className="mr-1" />
                              <span className="hidden sm:inline">Prévisualiser</span>
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleExecuteAutomation(automation.id, false)}
                              disabled={isExecuting || !automation.is_active || automation.actions?.length === 0}
                              className="bg-green-600 hover:bg-green-700"
                              title="Exécuter"
                              data-testid={`execute-${automation.id}`}
                            >
                              <Play size={16} className="mr-1" />
                              <span className="hidden sm:inline">Exécuter</span>
                            </Button>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleAutomation(automation.id)}
                              data-testid={`toggle-${automation.id}`}
                            >
                              {automation.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditAutomation(automation)}
                              data-testid={`edit-${automation.id}`}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDuplicateAutomation(automation)}
                              title="Dupliquer"
                              data-testid={`duplicate-${automation.id}`}
                            >
                              <Copy size={16} />
                            </Button>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setResetTrackingTarget(automation)}
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  data-testid={`reset-tracking-${automation.id}`}
                                >
                                  <RotateCw size={16} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Réinitialiser le tracking fire-once
                              </TooltipContent>
                            </Tooltip>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAutomation(automation.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-${automation.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="email" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => { setEditingItem(null); setEmailForm({ name: '', subject: '', html_content: '', text_content: '' }); setIsEmailDialogOpen(true); }}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                data-testid="add-email-template-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Nouveau Template Email
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {emailTemplates.length === 0 ? (
                <Card className="border-dashed col-span-full">
                  <CardContent className="text-center py-12">
                    <Mail className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Aucun template email</p>
                  </CardContent>
                </Card>
              ) : (
                emailTemplates.map((template) => (
                  <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`email-template-${template.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <div className="mt-1">
                            <VariableHighlighter text={template.subject} className="text-sm text-muted-foreground" />
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditEmailTemplate(template)} title="Modifier">
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDuplicateEmailTemplate(template)} title="Dupliquer">
                            <Copy size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleTestEmail(template)} title="Envoyer un test" className="text-primary">
                            <Play size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEmailTemplate(template.id)}
                            className="text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-muted-foreground line-clamp-3"
                           dangerouslySetInnerHTML={{ __html: template.html_content.substring(0, 200) }} />
                      {template.variables?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {template.variables.map((v, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{`{${v}}`}</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="sms" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => { setEditingItem(null); setSmsForm({ name: '', content: '' }); setIsSmsDialogOpen(true); }}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                data-testid="add-sms-template-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Nouveau Template SMS
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {smsTemplates.length === 0 ? (
                <Card className="border-dashed col-span-full">
                  <CardContent className="text-center py-12">
                    <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Aucun template SMS</p>
                  </CardContent>
                </Card>
              ) : (
                smsTemplates.map((template) => (
                  <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`sms-template-${template.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <CardDescription className="mt-1">
                            {template.character_count} caractères • {template.segment_count} segment(s)
                          </CardDescription>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditSmsTemplate(template)} title="Modifier">
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDuplicateSmsTemplate(template)} title="Dupliquer">
                            <Copy size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleTestSms(template)} title="Envoyer un test" className="text-primary">
                            <Play size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSmsTemplate(template.id)}
                            className="text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-muted-foreground">
                        <VariableHighlighter text={template.content} className="line-clamp-3" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            <Card className="mt-6 bg-amber-500/10 border-amber-500/20 dark:bg-amber-500/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground">Appel IA via Callkom</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ajoutez une action "Appel" dans vos automations pour déclencher un appel sortant via Callkom.
                      Collez l'URL API de votre campagne Callkom dans la configuration de l'action.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Part 3 — Sticky bulk action bar */}
        <AnimatePresence>
          {selectedIds.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-background border shadow-xl rounded-full px-4 py-2.5"
            >
              <span className="text-sm font-medium text-foreground px-2 whitespace-nowrap">
                {selectedIds.size} sélectionnée(s)
              </span>
              <div className="w-px h-5 bg-border" />
              <Button size="sm" variant="outline" onClick={() => handleBulkToggle(true)} className="rounded-full">
                <Power size={14} className="mr-1" /> Activer
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkToggle(false)} className="rounded-full">
                <PowerOff size={14} className="mr-1" /> Désactiver
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setIsBulkDeleteConfirmOpen(true)}
                className="rounded-full"
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={14} className="mr-1" /> Supprimer
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="rounded-full">
                <X size={14} className="mr-1" /> Annuler
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Automation dialog ── */}
        <Dialog open={isAutomationDialogOpen} onOpenChange={setIsAutomationDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editingItem ? 'Modifier Automation' : 'Nouvelle Automation'}
              </DialogTitle>
              <DialogDescription>
                Configurez les conditions de déclenchement et les actions à exécuter
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="auto-name">Nom de l'automation</Label>
                  <Input
                    id="auto-name"
                    value={automationForm.name}
                    onChange={(e) => setAutomationForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Suivi nouveau client"
                    data-testid="automation-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="trigger-mode">Mode de déclenchement</Label>
                  <Select
                    value={automationForm.trigger_mode}
                    onValueChange={(v) => setAutomationForm(prev => ({ ...prev, trigger_mode: v }))}
                  >
                    <SelectTrigger data-testid="trigger-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immédiat (sur changement)</SelectItem>
                      <SelectItem value="time_based">Temporel (basé sur une date)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="auto-desc">Description (optionnel)</Label>
                <Textarea
                  id="auto-desc"
                  value={automationForm.description}
                  onChange={(e) => setAutomationForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Décrivez le but de cette automation..."
                  rows={2}
                />
              </div>

              {automationForm.trigger_mode === 'time_based' && (
                <Card className="bg-primary/5 border-primary/20 dark:bg-primary/10">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock size={18} />
                      Configuration temporelle
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Colonne de date</Label>
                        <Select
                          value={automationForm.time_based_config?.date_column || ''}
                          onValueChange={(v) => setAutomationForm(prev => ({
                            ...prev,
                            time_based_config: { ...prev.time_based_config, date_column: v }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner une colonne" />
                          </SelectTrigger>
                          <SelectContent>
                            {sheetColumns.map((col) => (
                              <SelectItem key={col.name} value={col.name}>{col.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Avant ou après la date</Label>
                        <Select
                          value={automationForm.time_based_config?.before_or_after || 'after'}
                          onValueChange={(v) => setAutomationForm(prev => ({
                            ...prev,
                            time_based_config: { ...prev.time_based_config, before_or_after: v }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="before">Avant</SelectItem>
                            <SelectItem value="after">Après</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Décalage</Label>
                        <Input
                          type="number"
                          min="0"
                          value={automationForm.time_based_config?.offset_value || 0}
                          onChange={(e) => setAutomationForm(prev => ({
                            ...prev,
                            time_based_config: { ...prev.time_based_config, offset_value: parseInt(e.target.value) || 0 }
                          }))}
                        />
                      </div>
                      <div>
                        <Label>Unité</Label>
                        <Select
                          value={automationForm.time_based_config?.offset_unit || 'days'}
                          onValueChange={(v) => setAutomationForm(prev => ({
                            ...prev,
                            time_based_config: { ...prev.time_based_config, offset_unit: v }
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_UNITS.map((unit) => (
                              <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Heure d'exécution</Label>
                        <Input
                          type="time"
                          value={automationForm.time_based_config?.trigger_time || '09:00'}
                          onChange={(e) => setAutomationForm(prev => ({
                            ...prev,
                            time_based_config: { ...prev.time_based_config, trigger_time: e.target.value }
                          }))}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-medium">Conditions de déclenchement</Label>
                  <Button variant="outline" size="sm" onClick={addConditionGroup}>
                    <Plus size={14} className="mr-1" /> Ajouter un groupe
                  </Button>
                </div>

                {automationForm.condition_groups.length > 1 && (
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm text-muted-foreground">Lier les groupes par :</span>
                    <Select
                      value={automationForm.groups_logical_operator}
                      onValueChange={(v) => setAutomationForm(prev => ({ ...prev, groups_logical_operator: v }))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">ET</SelectItem>
                        <SelectItem value="OR">OU</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-4">
                  {automationForm.condition_groups.map((group, groupIndex) => (
                    <Card key={groupIndex} className="bg-muted/50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-foreground">Groupe {groupIndex + 1}</span>
                          <div className="flex items-center gap-2">
                            {group.conditions.length > 1 && (
                              <Select
                                value={group.logical_operator}
                                onValueChange={(v) => {
                                  const newGroups = [...automationForm.condition_groups];
                                  newGroups[groupIndex].logical_operator = v;
                                  setAutomationForm(prev => ({ ...prev, condition_groups: newGroups }));
                                }}
                              >
                                <SelectTrigger className="w-20 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="AND">ET</SelectItem>
                                  <SelectItem value="OR">OU</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            {automationForm.condition_groups.length > 1 && (
                              <Button variant="ghost" size="sm" onClick={() => removeConditionGroup(groupIndex)}>
                                <X size={14} />
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          {group.conditions.map((condition, conditionIndex) => (
                            <div key={conditionIndex} className="flex flex-wrap items-center gap-2">
                              <Select
                                value={condition.column_name}
                                onValueChange={(v) => updateCondition(groupIndex, conditionIndex, 'column_name', v)}
                              >
                                <SelectTrigger className="w-32 sm:w-40">
                                  <SelectValue placeholder="Colonne" />
                                </SelectTrigger>
                                <SelectContent>
                                  {sheetColumns.map((col) => (
                                    <SelectItem key={col.name} value={col.name}>{col.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              <Select
                                value={condition.operator}
                                onValueChange={(v) => updateCondition(groupIndex, conditionIndex, 'operator', v)}
                              >
                                <SelectTrigger className="w-32 sm:w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {OPERATORS.map((op) => (
                                    <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                                <Input
                                  value={condition.value || ''}
                                  onChange={(e) => updateCondition(groupIndex, conditionIndex, 'value', e.target.value)}
                                  placeholder="Valeur"
                                  className="w-28 sm:w-32"
                                />
                              )}

                              {group.conditions.length > 1 && (
                                <Button variant="ghost" size="sm" onClick={() => removeCondition(groupIndex, conditionIndex)}>
                                  <X size={14} />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>

                        <Button variant="ghost" size="sm" className="mt-2" onClick={() => addCondition(groupIndex)}>
                          <Plus size={14} className="mr-1" /> Ajouter une condition
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-medium">Actions à exécuter</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => addAction('email')}>
                      <Mail size={14} className="mr-1" /> Email
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => addAction('sms')}>
                      <MessageSquare size={14} className="mr-1" /> SMS
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => addAction('ai_call')}>
                      <Phone size={14} className="mr-1" /> Appel
                    </Button>
                  </div>
                </div>

                {automationForm.actions.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="text-center py-6">
                      <p className="text-muted-foreground text-sm">Aucune action configurée</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {automationForm.actions.map((action, index) => (
                      <Card key={index} className="bg-muted/50">
                        <CardContent className="p-3 flex items-center gap-3">
                          {action.action_type === 'email' && <Mail size={18} className="text-primary" />}
                          {action.action_type === 'sms' && <MessageSquare size={18} className="text-green-500" />}
                          {action.action_type === 'ai_call' && <Phone size={18} className="text-amber-500" />}

                          {action.action_type === 'ai_call' ? (
                            <div className="flex-1 flex flex-col sm:flex-row gap-2">
                              <Input
                                value={action.callkom_url || ''}
                                onChange={(e) => updateAction(index, 'callkom_url', e.target.value)}
                                placeholder="URL Callkom (copiez depuis Callkom > Intégration)"
                                className="flex-1 text-xs"
                              />
                              <Select
                                value={action.scenario || ''}
                                onValueChange={(v) => updateAction(index, 'scenario', v)}
                              >
                                <SelectTrigger className="sm:w-56 text-xs" data-testid={`scenario-select-${index}`}>
                                  <SelectValue placeholder="Scénario" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="bienvenue_google">Bienvenue + Avis Google</SelectItem>
                                  <SelectItem value="bilan">Bilan / Point contrat</SelectItem>
                                  <SelectItem value="reactivation">Réactivation</SelectItem>
                                  <SelectItem value="autre">Autre / non spécifié</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <Select
                              value={action.template_id || ''}
                              onValueChange={(v) => updateAction(index, 'template_id', v)}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder={`Sélectionner un template ${action.action_type === 'email' ? 'email' : 'SMS'}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {(action.action_type === 'email' ? emailTemplates : smsTemplates).map((t) => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          <Switch
                            checked={action.is_enabled}
                            onCheckedChange={(checked) => updateAction(index, 'is_enabled', checked)}
                          />

                          <Button variant="ghost" size="sm" onClick={() => removeAction(index)}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="is-active"
                  checked={automationForm.is_active}
                  onCheckedChange={(checked) => setAutomationForm(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is-active" className="cursor-pointer">Activer cette automation</Label>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setIsAutomationDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSaveAutomation} className="bg-primary text-white" data-testid="save-automation-btn">
                <Save size={16} className="mr-2" />
                {editingItem ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Email template dialog ── */}
        <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Modifier Template Email' : 'Nouveau Template Email'}</DialogTitle>
              <DialogDescription>Créez un template d'email avec des variables dynamiques</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="email-name">Nom du template</Label>
                <Input
                  id="email-name"
                  value={emailForm.name}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Email de bienvenue"
                  data-testid="email-template-name"
                />
              </div>

              <EmailSubjectWithTags
                value={emailForm.subject}
                onChange={(subject) => setEmailForm(prev => ({ ...prev, subject }))}
              />

              <EmailEditor
                value={emailForm.html_content}
                onChange={(content) => setEmailForm(prev => ({ ...prev, html_content: content }))}
                placeholder="Rédigez votre email ici..."
              />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsEmailDialogOpen(false)}>Annuler</Button>
              {editingItem && (
                <Button
                  variant="outline"
                  onClick={() => handleTestEmail({ subject: emailForm.subject, html_content: emailForm.html_content, text_content: emailForm.text_content })}
                  data-testid="test-email-btn"
                >
                  <Play size={16} className="mr-2" />
                  Envoyer un test
                </Button>
              )}
              <Button onClick={handleSaveEmailTemplate} className="bg-primary text-white" data-testid="save-email-template-btn">
                <Save size={16} className="mr-2" />
                {editingItem ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── SMS template dialog ── */}
        <Dialog open={isSmsDialogOpen} onOpenChange={setIsSmsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Modifier Template SMS' : 'Nouveau Template SMS'}</DialogTitle>
              <DialogDescription>Créez un template SMS avec compteur de caractères</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="sms-name">Nom du template</Label>
                <Input
                  id="sms-name"
                  value={smsForm.name}
                  onChange={(e) => setSmsForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: SMS de rappel"
                  data-testid="sms-template-name"
                />
              </div>

              <SmsEditorWithTags
                value={smsForm.content}
                onChange={(content) => setSmsForm(prev => ({ ...prev, content }))}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsSmsDialogOpen(false)}>Annuler</Button>
              {editingItem && (
                <Button
                  variant="outline"
                  onClick={() => handleTestSms({ content: smsForm.content })}
                  data-testid="test-sms-btn"
                >
                  <Play size={16} className="mr-2" />
                  Envoyer un test
                </Button>
              )}
              <Button onClick={handleSaveSmsTemplate} className="bg-primary text-white" data-testid="save-sms-template-btn">
                <Save size={16} className="mr-2" />
                {editingItem ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Preview / Execution dialog ── */}
        <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {previewData?.is_execution_result ? (
                  <><CheckCircle2 className="text-green-600" /> Résultat d'exécution</>
                ) : (
                  <><Eye className="text-primary" /> Prévisualisation</>
                )}
              </DialogTitle>
              <DialogDescription>
                {previewData?.automation_name}
              </DialogDescription>
            </DialogHeader>

            {previewData && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-primary/10">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-primary">
                        {previewData.total_clients || previewData.clients_processed || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Clients analysés</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-500/10">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-500">
                        {previewData.matching_count || previewData.clients_matched || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Clients correspondants</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-foreground">
                        {((previewData.matching_count || previewData.clients_matched || 0) / (previewData.total_clients || previewData.clients_processed || 1) * 100).toFixed(1)}%
                      </p>
                      <p className="text-sm text-muted-foreground">Taux de correspondance</p>
                    </CardContent>
                  </Card>
                </div>

                {previewData.matching_clients && previewData.matching_clients.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Users size={16} />
                      Clients correspondants ({previewData.matching_clients.length})
                    </h4>
                    <div className="bg-muted/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-muted-foreground">
                            <th className="pb-2">Nom</th>
                            <th className="pb-2">Email</th>
                            <th className="pb-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.matching_clients.map((client, idx) => (
                            <tr key={idx} className="border-t border-border">
                              <td className="py-2">{client.name}</td>
                              <td className="py-2 text-muted-foreground">{client.email}</td>
                              <td className="py-2">
                                <Badge variant="outline" className="text-xs">{client.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {previewData.results && previewData.results.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Zap size={16} />
                      Résultats d'exécution ({previewData.results.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {previewData.results.map((result, idx) => (
                        <div key={idx} className="bg-muted/50 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{result.client_name}</p>
                              <p className="text-sm text-muted-foreground">{result.client_email}</p>
                            </div>
                            <div className="text-right flex flex-wrap gap-1 justify-end">
                              {result.actions?.map((action, aIdx) => (
                                <Badge
                                  key={aIdx}
                                  variant={action.status === 'success' ? 'default' : 'destructive'}
                                >
                                  {action.action_type === 'email' && <Mail size={12} className="mr-1" />}
                                  {action.action_type === 'sms' && <MessageSquare size={12} className="mr-1" />}
                                  {action.action_type === 'ai_call' && <Phone size={12} className="mr-1" />}
                                  {action.status}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          {result.actions?.some(a => a.status === 'failed' && a.result?.error) && (
                            <div className="mt-2 space-y-1">
                              {result.actions.filter(a => a.status === 'failed' && a.result?.error).map((a, i) => (
                                <p key={i} className="text-xs text-red-400 bg-red-500/10 rounded px-2 py-1">
                                  {a.action_type === 'ai_call' ? 'Appel' : a.action_type === 'sms' ? 'SMS' : 'Email'} : {a.result.error}
                                </p>
                              ))}
                            </div>
                          )}
                          {result.actions?.some(a => a.sender) && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Expéditeur: {result.actions.find(a => a.sender)?.sender}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {previewData.dry_run && (
                  <Card className="bg-amber-500/10 border-amber-500/20 dark:bg-amber-500/5">
                    <CardContent className="p-3">
                      <p className="text-sm text-foreground">
                        <AlertCircle size={14} className="inline mr-1" />
                        <strong>Mode simulation :</strong> Aucun email/SMS n'a été envoyé.
                        Cliquez sur "Exécuter" pour lancer l'envoi réel.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPreviewDialogOpen(false)}>Fermer</Button>
              {!previewData?.is_execution_result && previewData?.matching_count > 0 && (
                <Button
                  onClick={() => {
                    setIsPreviewDialogOpen(false);
                    handleExecuteAutomation(previewData.automation_id, false);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Play size={16} className="mr-2" />
                  Exécuter maintenant
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Part 1 — Import result modal ── */}
        <Dialog open={isImportResultOpen} onOpenChange={() => {}}>
          <DialogContent className="max-w-lg" data-testid="import-result-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="text-green-600" size={20} />
                Import terminé
              </DialogTitle>
            </DialogHeader>

            {importResult && (
              <div className="space-y-4">
                {/* Success summary */}
                <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-4 space-y-1">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">
                    ✅ {importResult.created_email_templates ?? 0} template(s) email créé(s),{' '}
                    {importResult.updated_email_templates ?? 0} mis à jour
                  </p>
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">
                    ✅ {importResult.created_automations ?? 0} automation(s) créée(s)
                  </p>
                </div>

                {/* Error summary */}
                {importResult.errors?.length > 0 && (
                  <div className="rounded-lg bg-orange-500/10 border border-orange-500/20 p-4">
                    <button
                      className="flex w-full items-center justify-between text-sm font-medium text-orange-700 dark:text-orange-400"
                      onClick={() => setImportErrorExpanded(v => !v)}
                    >
                      <span>⚠️ {importResult.errors.length} erreur(s) détectée(s)</span>
                      {importErrorExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {importErrorExpanded && (
                      <ul className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                        {importResult.errors.map((err, i) => (
                          <li key={i} className="text-xs bg-background rounded p-2 border">
                            <span className="font-medium">{err.name || err.id || `#${err.index ?? i}`}</span>
                            <span className="text-muted-foreground"> — {err.error}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2 mt-2">
              {importResult?.errors?.some(e => e.type === 'automation') && (
                <Button
                  variant="outline"
                  onClick={handleShowBrokenAutomations}
                  data-testid="show-broken-automations-btn"
                >
                  <AlertTriangle size={14} className="mr-2 text-orange-500" />
                  Voir les automations en erreur
                </Button>
              )}
              <Button onClick={handleCloseImportResult} data-testid="close-import-result-btn">
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Part 3 — Bulk delete confirmation modal ── */}
        <Dialog open={isBulkDeleteConfirmOpen} onOpenChange={setIsBulkDeleteConfirmOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={20} />
                Confirmer la suppression
              </DialogTitle>
              <DialogDescription>
                Supprimer {selectedIds.size} automation(s) ? Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsBulkDeleteConfirmOpen(false)}>
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={handleBulkDelete}
                data-testid="confirm-bulk-delete-btn"
              >
                <Trash2 size={14} className="mr-2" />
                Supprimer {selectedIds.size} automation(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Reset fire-once tracking confirmation modal ── */}
        <Dialog open={!!resetTrackingTarget} onOpenChange={(open) => !open && setResetTrackingTarget(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="text-orange-500" size={20} />
                Réinitialiser le tracking
              </DialogTitle>
              <DialogDescription>
                Tous les clients pourront recevoir à nouveau les actions de cette automation.
                Cela peut générer des doublons. Continuer ?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetTrackingTarget(null)}>
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={handleResetTracking}
                data-testid="confirm-reset-tracking-btn"
              >
                <RotateCw size={14} className="mr-2" />
                Réinitialiser
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </TooltipProvider>
  );
};

export default AdvancedAutomations;
